/**
 * useAuth — the sign-in spine.
 *
 *   phone → OTP  → tokens + user      (the original path)
 *   phone → PIN  → tokens + user      (works with no SMS provider, and on a
 *                                      counter with no mobile signal)
 *   phone + PIN  → awaiting approval  (registration; deliberately no tokens)
 *
 * It deliberately stops there. Everything downstream — onboarding status,
 * store selection, device registration, database activation — belongs to the
 * gate in app/App.tsx and the screens under screens/onboarding/.
 *
 * It did once own store selection too, and that had to go: the moment
 * `setCurrentUser` resolves, App.tsx re-renders with a session and routes to
 * StoreGate, so any store work still running here raced a screen that was
 * already doing the same job. One owner, not two. StoreGate also handles the
 * cases this hook could not see — a merchant who is not approved yet, and a
 * reload that restored a session whose store was never chosen.
 *
 * ── Why login and register are separate buttons ────────────────────────────
 * The OTP path can afford one set of fields for both, because `verify-otp`
 * creates a PENDING merchant for any number it has never seen. PIN login
 * cannot: telling an unknown number apart from a wrong PIN is exactly the
 * thing the server refuses to do, so that the endpoint cannot be used to
 * discover which numbers are registered. The person therefore has to say which
 * one they are, which is also plainer to read at a counter.
 */
import { useCallback, useState } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { tokenStore } from '@/services/tokenStore';
import { describeError, isTechnicalMessage } from '@/utils/errorMessage';
import { HttpError } from '@/services/httpClient';
import { requestOtp, verifyOtp, loginWithPin, registerWithPin } from './authApi';

export type AuthStage =
  /** Entering the mobile number. */
  | 'phone'
  /** Entering the code that was texted. */
  | 'otp'
  /** Entering the PIN for an existing account. */
  | 'pin'
  /** Choosing a PIN for a new account. */
  | 'register'
  /** Registered; nothing more to do until an admin approves. */
  | 'registered';

interface AuthState {
  stage: AuthStage;
  phone: string;
  busy: boolean;
  error: string | null;
  /** Set when the account is locked, so the screen can stop offering retries. */
  locked: boolean;
  /**
   * Set when registration was refused because the number already has a PIN.
   * The screen offers a one-tap way back to signing in rather than leaving the
   * person to read their way out of it.
   */
  alreadyRegistered: boolean;
  /** Shown after registering — the server's own wording. */
  notice: string | null;
}

const INITIAL: AuthState = {
  stage: 'phone',
  phone: '',
  busy: false,
  error: null,
  locked: false,
  alreadyRegistered: false,
  notice: null,
};

/** The server's machine-readable reason, when it sent one. */
function codeOf(err: unknown): string | null {
  if (!(err instanceof HttpError)) return null;
  const body = err.body as { code?: string } | null;
  return typeof body?.code === 'string' ? body.code : null;
}

/**
 * Turns an HttpError into something a cashier can act on.
 *
 * This used to return the server's `body.message` unconditionally, before ever
 * consulting the status code. That is right for a validation message and wrong
 * for a framework one: a throttled sign-in answers with
 * "ThrottlerException: Too Many Requests", and that string went straight onto
 * the login screen. describeError keeps the useful server copy and drops the
 * framework's; the sign-in specific wording stays here.
 */
function describe(err: unknown, credential: 'OTP' | 'PIN' = 'OTP'): string {
  if (err instanceof HttpError) {
    // The server's own words still win when they were written for a person.
    // For PIN this matters more than usual: the message carries the countdown
    // ("2 attempts left before this account is locked"), which nothing on the
    // client could reconstruct. Only FRAMEWORK strings are dropped.
    const body = err.body as { message?: string | string[] } | null;
    const raw = Array.isArray(body?.message) ? body?.message[0] : body?.message;
    if (typeof raw === 'string' && raw.trim() && !isTechnicalMessage(raw)) return raw;

    // Sign-in specific copy, which reads better here than describeError's
    // general "your session has expired". Named after the credential actually
    // being checked — telling someone their OTP is wrong when they typed a PIN
    // is its own small confusion.
    if (err.status === 401) {
      return credential === 'PIN' ? 'Incorrect mobile number or PIN.' : 'Incorrect or expired OTP.';
    }
    if (err.status === 429) return 'Too many attempts. Wait a moment and try again.';
  }
  return describeError(err);
}

export function useAuth() {
  const [state, setState] = useState<AuthState>(INITIAL);

  const setAuthToken = useAppStore((s) => s.setAuthToken);
  const setRefreshToken = useAppStore((s) => s.setRefreshToken);
  const setCurrentUser = useAppStore((s) => s.setCurrentUser);
  const logoutStore = useAppStore((s) => s.logout);

  /** Hands the session to App.tsx, which takes over routing on the next render. */
  const adoptSession = useCallback(
    async (res: { access_token: string; refresh_token: string; user: Parameters<typeof setCurrentUser>[0] }) => {
      setAuthToken(res.access_token);
      setRefreshToken(res.refresh_token);
      await setCurrentUser(res.user);
      // No `setState` after this: the component is about to unmount.
    },
    [setAuthToken, setRefreshToken, setCurrentUser],
  );

  /** Step 1 — send the OTP. */
  const sendOtp = useCallback(async (phone: string) => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      await requestOtp(phone);
      setState((s) => ({ ...s, stage: 'otp', phone, busy: false }));
    } catch (err) {
      setState((s) => ({ ...s, busy: false, error: describe(err) }));
    }
  }, []);

  /**
   * Step 2 — exchange the OTP for tokens and the user record.
   *
   * The store list in the response is ignored here on purpose; StoreGate
   * re-reads it from `/auth/check-status`, which is also correct after an
   * approval that happened while this tab was open.
   */
  const submitOtp = useCallback(
    async (otp: string) => {
      setState((s) => ({ ...s, busy: true, error: null }));
      try {
        await adoptSession(await verifyOtp(state.phone, otp));
      } catch (err) {
        setState((s) => ({ ...s, busy: false, error: describe(err) }));
      }
    },
    [state.phone, adoptSession],
  );

  /** Move to the PIN field for a number that already has an account. */
  const goToPin = useCallback((phone: string) => {
    setState((s) => ({ ...s, stage: 'pin', phone, error: null, locked: false, alreadyRegistered: false }));
  }, []);

  /** Move to the "choose a PIN" form for a new account. */
  const goToRegister = useCallback((phone: string) => {
    setState((s) => ({ ...s, stage: 'register', phone, error: null, locked: false, alreadyRegistered: false }));
  }, []);

  /**
   * Exchange phone + PIN for a session.
   *
   * Two refusals are not ordinary errors and are surfaced as such:
   * ACCOUNT_LOCKED means retrying is pointless until support resets the PIN,
   * and AWAITING_APPROVAL means the PIN was right and the wait is the problem.
   */
  const submitPin = useCallback(
    async (pin: string) => {
      setState((s) => ({ ...s, busy: true, error: null }));
      try {
        await adoptSession(await loginWithPin(state.phone, pin));
      } catch (err) {
        setState((s) => ({
          ...s,
          busy: false,
          error: describe(err, 'PIN'),
          locked: codeOf(err) === 'ACCOUNT_LOCKED',
        }));
      }
    },
    [state.phone, adoptSession],
  );

  /**
   * Register a number with a PIN.
   *
   * No session follows, by design: the account is created PENDING and an admin
   * has to approve it. The screen shows the server's own wording rather than
   * inventing its own, so the two cannot drift.
   */
  const submitRegistration = useCallback(
    async (phone: string, pin: string) => {
      setState((s) => ({ ...s, busy: true, error: null }));
      try {
        const res = await registerWithPin(phone, pin);
        setState((s) => ({ ...s, stage: 'registered', phone, busy: false, notice: res.message }));
      } catch (err) {
        setState((s) => ({
          ...s,
          busy: false,
          error: describe(err, 'PIN'),
          alreadyRegistered: codeOf(err) === 'ALREADY_REGISTERED',
        }));
      }
    },
    [],
  );

  /**
   * Clears the session but NOT local data (BIGXBOOK_WEB_PLAN §7.1, Decision 3):
   * a browser can hold invoices that exist nowhere else, and the merchant is
   * expected to log back in on the same device.
   */
  const logout = useCallback(async () => {
    tokenStore.clear();
    await logoutStore();
    setState(INITIAL);
  }, [logoutStore]);

  const back = useCallback(() => {
    setState((s) => ({
      ...s,
      stage: 'phone',
      error: null,
      locked: false,
      alreadyRegistered: false,
      notice: null,
    }));
  }, []);

  return {
    ...state,
    sendOtp,
    submitOtp,
    goToPin,
    goToRegister,
    submitPin,
    submitRegistration,
    logout,
    back,
  };
}
